import React, { useState, useEffect } from 'react';
import { apiGet, apiPost } from '../services/api';

const pad = n => String(n).padStart(2, '0');
const parseDT = s => (s ? new Date(String(s).replace(' ', 'T')) : null);
const toInputDT = s => { const d = parseDT(s); return d ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}` : ''; };
const fromInputDT = v => (v ? v.replace('T', ' ') + ':00' : null);
const fmtDT = s => { const d = parseDT(s); return d ? d.toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'; };
const V_ST = { pendiente: 'Pendiente', confirmada: 'Confirmada', completada: 'Completada', reagendada: 'Reagendada', en_curso: 'En curso' };

const box    = { border: '1px solid #e2e6ea', borderRadius: 12, padding: 14, margin: '0 0 14px', background: '#fff' };
const ttl    = { fontSize: '.72rem', textTransform: 'uppercase', letterSpacing: '.06em', color: '#8a94a6', fontWeight: 700, marginBottom: 8 };
const inputS = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, marginBottom: 8 };
const btn    = (bg, c) => ({ background: bg, color: c, border: c === '#fff' ? 'none' : `1px solid ${c}`, borderRadius: 8, padding: '9px 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer' });
const rowS   = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '8px 0', borderTop: '1px solid #f1f5f9', fontSize: 13 };

// Evita que un fallo aquí tumbe toda la ficha del lead
class Boundary extends React.Component {
  constructor(p) { super(p); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err, info) { console.error('[LeadInstallerBox] error:', err, info); }
  render() {
    if (this.state.err) return (
      <div style={{ ...box, color: '#b45309', background: '#fffbeb', fontSize: 12 }}>
        Panel de visitas/instalaciones no disponible.<br />
        <span style={{ color: '#94a3b8' }}>{String(this.state.err && this.state.err.message || this.state.err)}</span>
      </div>
    );
    return this.props.children;
  }
}

export default function LeadInstallerBox(props) {
  return <Boundary><InstallerBoxInner {...props} /></Boundary>;
}

function InstallerBoxInner({ lead, token, isAdmin, onChanged }) {
  const [installers, setInstallers] = useState([]);
  const [mode, setMode] = useState(null); // 'visita' | 'install'
  const [form, setForm] = useState({ installer_id: '', scheduled_at: '', days: 1, address: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (isAdmin) apiGet('/installers', token).then(d => setInstallers(Array.isArray(d) ? d : [])).catch(() => {});
  }, [isAdmin, token]);

  const visits = Array.isArray(lead.visits) ? lead.visits : [];
  const installations = Array.isArray(lead.installations) ? lead.installations : [];
  const decided = lead.visitDecision;
  const isReservado = lead.status === 'reservado';
  const isPrimerPago = lead.status === 'primer_pago' || lead.status === 'firmado' || lead.status === 'ganado';
  const plannedDays = (visits.find(v => v.planned_install_days) || {}).planned_install_days || 1;

  function openForm(kind) {
    setForm({ installer_id: installers[0]?.id || '', scheduled_at: '', days: kind === 'install' ? plannedDays : 1, address: lead.city || '' });
    setErr(''); setMode(kind);
  }

  async function decideVisita(decision) {
    setBusy(true); setErr('');
    try {
      if (decision === 'visita' && (!form.installer_id || !form.scheduled_at)) { setErr('Elige instalador y día/hora'); setBusy(false); return; }
      const body = decision === 'sin_visita' ? { decision: 'sin_visita' } : {
        decision: 'visita', installer_id: form.installer_id, scheduled_at: fromInputDT(form.scheduled_at),
        planned_install_days: Number(form.days) || null, address: form.address,
      };
      await apiPost(`/leads/${lead.id}/visit-decision`, body, token);
      setMode(null); onChanged();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }

  async function scheduleInstall() {
    setBusy(true); setErr('');
    try {
      if (!form.installer_id || !form.scheduled_at) { setErr('Elige instalador y día/hora'); setBusy(false); return; }
      await apiPost('/installations', { lead_id: lead.id, installer_id: form.installer_id, scheduled_at: fromInputDT(form.scheduled_at), duration_days: Number(form.days) || 1 }, token);
      setMode(null); onChanged();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }

  const installerSelect = (
    <select style={inputS} value={form.installer_id} onChange={e => setForm(f => ({ ...f, installer_id: e.target.value }))}>
      <option value="">— Elige instalador —</option>
      {installers.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
    </select>
  );

  return (
    <div style={box}>
      <div style={ttl}>Visitas e instalaciones</div>

      {/* Decisión de visita tras la reserva */}
      {isReservado && decided !== 'visita' && decided !== 'sin_visita' && (
        isAdmin ? (
          mode === 'visita' ? (
            <div style={{ marginBottom: 10 }}>
              {installerSelect}
              <input type="datetime-local" style={inputS} value={form.scheduled_at} onChange={e => setForm(f => ({ ...f, scheduled_at: e.target.value }))} />
              <input type="number" min="1" max="6" style={inputS} placeholder="Días de instalación previstos" value={form.days} onChange={e => setForm(f => ({ ...f, days: e.target.value }))} />
              <input style={inputS} placeholder="Dirección de la visita" value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} />
              {err && <p style={{ color: '#dc2626', fontSize: 12 }}>{err}</p>}
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={btn('#fff', '#64748b')} onClick={() => setMode(null)} disabled={busy}>Cancelar</button>
                <button style={btn('#16a34a', '#fff')} onClick={() => decideVisita('visita')} disabled={busy}>Programar visita</button>
              </div>
            </div>
          ) : (
            <div style={{ background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 10, padding: 12, marginBottom: 10 }}>
              <div style={{ fontSize: 13, color: '#92400e', marginBottom: 8, fontWeight: 600 }}>⚠️ Reserva registrada. Decide la visita (obligatorio para avanzar a primer pago):</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={btn('#E84300', '#fff')} onClick={() => openForm('visita')}>Programar visita</button>
                <button style={btn('#fff', '#64748b')} onClick={() => decideVisita('sin_visita')} disabled={busy}>Sin visita</button>
              </div>
              {err && <p style={{ color: '#dc2626', fontSize: 12, marginTop: 6 }}>{err}</p>}
            </div>
          )
        ) : (
          <div style={{ background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 10, padding: 12, marginBottom: 10, fontSize: 13, color: '#92400e' }}>
            Reserva registrada · pendiente de que el administrador decida la visita.
          </div>
        )
      )}

      {decided === 'sin_visita' && <div style={{ ...rowS, borderTop: 'none', color: '#64748b' }}>Marcado como <b>sin visita técnica</b>.</div>}

      {/* Listado de visitas */}
      {visits.map(v => (
        <div key={v.id} style={rowS}>
          <div>📋 Visita · {fmtDT(v.scheduled_at)}<div style={{ color: '#94a3b8', fontSize: 12 }}>{v.installer_name || 'Sin instalador'}{v.planned_install_days ? ` · ${v.planned_install_days} día(s) prev.` : ''}</div></div>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>{V_ST[v.status] || v.status}</span>
        </div>
      ))}

      {/* Programar instalación (primer pago) */}
      {isPrimerPago && isAdmin && (
        mode === 'install' ? (
          <div style={{ marginTop: 10 }}>
            {installerSelect}
            <input type="datetime-local" style={inputS} value={form.scheduled_at} onChange={e => setForm(f => ({ ...f, scheduled_at: e.target.value }))} />
            <input type="number" min="1" max="6" style={inputS} placeholder="Nº de días" value={form.days} onChange={e => setForm(f => ({ ...f, days: e.target.value }))} />
            {err && <p style={{ color: '#dc2626', fontSize: 12 }}>{err}</p>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={btn('#fff', '#64748b')} onClick={() => setMode(null)} disabled={busy}>Cancelar</button>
              <button style={btn('#16a34a', '#fff')} onClick={scheduleInstall} disabled={busy}>Programar instalación</button>
            </div>
          </div>
        ) : (
          <button style={{ ...btn('#E84300', '#fff'), marginTop: 10 }} onClick={() => openForm('install')}>🔧 Programar instalación</button>
        )
      )}

      {/* Listado de instalaciones */}
      {installations.map(n => (
        <div key={n.id} style={rowS}>
          <div>🔧 Instalación · {fmtDT(n.scheduled_at)}<div style={{ color: '#94a3b8', fontSize: 12 }}>{n.installer_name || 'Sin instalador'}{n.duration_days > 1 ? ` · ${n.duration_days} días` : ''}</div></div>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>{V_ST[n.status] || n.status}</span>
        </div>
      ))}

      {!isReservado && !isPrimerPago && !visits.length && !installations.length && decided !== 'sin_visita' && (
        <div style={{ fontSize: 13, color: '#94a3b8' }}>Sin visitas ni instalaciones. Aparecerán al reservar (visita) o al pasar a primer pago (instalación).</div>
      )}
    </div>
  );
}
